/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { FormControl, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { FC } from "react";

import { Lookup, LookupValue } from "../../api/types/types";

export const findLookupValue = (
  lookup: Lookup | undefined,
  selectedValue: string
): LookupValue | undefined => {
  if (!lookup || !selectedValue) {
    return undefined;
  }
  return lookup.values.find(
    (item) => item.value?.toString() === selectedValue || item.id === selectedValue
  );
};

export const getLookupValueString = (item: LookupValue): string => {
  return item.value?.toString() || item.id || "";
};

export interface LookupSelectProps {
  label: string;
  value: string;
  lookup: Lookup | undefined;
  campaignKey: string;
  placeholder: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  width?: { xs: string; sm: string };
  error?: boolean;
}

const LookupSelect: FC<LookupSelectProps> = ({
  label,
  value,
  lookup,
  campaignKey,
  placeholder,
  disabled = false,
  onChange,
  width = { xs: "100%", sm: "calc(50% - 0.5rem)" },
  error = false,
}) => {
  const selectedItem = findLookupValue(lookup, value);

  return (
    <FormControl sx={{ width }} disabled={disabled} error={error}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Select
        value={value || ""}
        displayEmpty
        disabled={disabled}
        error={error}
        sx={{
          "&.Mui-disabled": {
            backgroundColor: colors.primary50,
          },
        }}
        renderValue={(selected) => {
          if (!selected) {
            return <Typography color="text.secondary">{placeholder}</Typography>;
          }
          return <Typography>{selectedItem?.label || selected}</Typography>;
        }}
        onChange={(e: SelectChangeEvent<string>) => onChange(e.target.value)}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              maxHeight: "18.75rem",
              "& .MuiMenuItem-root": {
                whiteSpace: "normal",
              },
            },
          },
          disableAutoFocusItem: true,
        }}
      >
        {lookup?.values.map((item) => (
          <MenuItem
            key={`${campaignKey}-${item.value || item.id}`}
            value={getLookupValueString(item)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LookupSelect;
